/**
 * Spec 200 Fase 5 #1 — single editable text field for WorkDetail.
 *
 * Two visual states:
 *   1. read mode → renders the value as a plain `<span>`/`<p>`. Click
 *      enters edit mode.
 *   2. edit mode → renders an `<input>` (single-line) or `<textarea>`
 *      (multi-line) prefilled with the current value. Save on blur OR
 *      on Enter (single-line) / Cmd+Enter (multi-line). Esc cancels.
 *
 * The component is uncontrolled internally (its own state during
 * edit). The parent supplies the canonical value; on save we call
 * `onSave(newValue)` and the parent decides whether to apply the
 * change (validation, optimistic update, persistence).
 *
 * Validation contract:
 *   - `validate(value)` returns `null` when ok, otherwise an error
 *     string the component renders in red below the input.
 *   - When validation fails, save is blocked but the user stays in
 *     edit mode so they can fix it. Esc still backs out without
 *     persistence.
 *
 * Accessibility:
 *   - The read-mode trigger is a real `<button>` so screen readers
 *     announce it as interactive. We keep the visual identical to
 *     the surrounding text via CSS (no border, no chrome).
 *   - The active input/textarea takes focus immediately on enter.
 *
 * Why not a state library:
 *   The component owns ~40 lines of state. A controlled equivalent
 *   would push every keystroke up to the parent and back; the
 *   uncontrolled-during-edit pattern keeps re-renders local.
 */
import { useEffect, useRef, useState } from 'react';

import './InlineEditField.css';

export interface InlineEditFieldProps {
  value: string;
  onSave: (next: string) => void | Promise<void>;
  /** Visible placeholder while the value is empty. */
  placeholder?: string;
  /** Multi-line (textarea) when true. Default: false (single-line). */
  multiline?: boolean;
  /** Optional validator. Return null on ok, otherwise an error
   *  message to render. */
  validate?: (value: string) => string | null;
  /** What kind of element wraps the displayed value in read mode.
   *  Defaults to `span` so it composes inside any text context. */
  as?: 'span' | 'p' | 'h1' | 'h2';
  /** Forwarded to the read-mode trigger AND the input — lets E2E
   *  drive both halves with a single id. */
  testId?: string;
  /** Optional label used by screen readers when read-mode shows the
   *  empty placeholder. */
  ariaLabel?: string;
  /** Forwarded to the input/textarea (e.g. autocomplete, inputMode).
   *  Limited to safe HTML attrs. */
  inputAttrs?: React.InputHTMLAttributes<HTMLInputElement>;
  textareaAttrs?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}

export function InlineEditField({
  value,
  onSave,
  placeholder = '',
  multiline = false,
  validate,
  as: As = 'span',
  testId,
  ariaLabel,
  inputAttrs,
  textareaAttrs,
}: InlineEditFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // When the parent updates the value externally (a sync arrives
  // mid-edit, say) we update the read-mode display BUT don't trample
  // an active edit — the user's typing wins until they save or cancel.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Move focus + cursor-to-end when entering edit mode. Tests rely on
  // the input being focused so user.type goes to the right place.
  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    // Move caret to end so a tap doesn't clear the existing value.
    const len = el.value.length;
    el.setSelectionRange(len, len);
  }, [editing]);

  function start() {
    setDraft(value);
    setError(null);
    setEditing(true);
  }

  async function save() {
    const next = multiline ? draft : draft.trim();
    if (validate) {
      const v = validate(next);
      if (v) {
        setError(v);
        return;
      }
    }
    setEditing(false);
    setError(null);
    if (next !== value) {
      await onSave(next);
    }
  }

  function cancel() {
    setDraft(value);
    setError(null);
    setEditing(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === 'Enter') {
      // single-line: Enter saves. multi-line: Enter inserts newline,
      // Cmd/Ctrl+Enter saves.
      if (!multiline) {
        e.preventDefault();
        void save();
      } else if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        void save();
      }
    }
  }

  if (!editing) {
    const display = value || placeholder;
    return (
      <button
        type="button"
        className={`ief-trigger ${value ? '' : 'ief-trigger--empty'}`}
        onClick={start}
        data-testid={testId ? `${testId}-trigger` : undefined}
        aria-label={ariaLabel ?? (value ? undefined : placeholder)}
      >
        <As className="ief-display">{display}</As>
      </button>
    );
  }

  return (
    <div className="ief-edit">
      {multiline ? (
        <textarea
          ref={(el) => {
            inputRef.current = el;
          }}
          className="ief-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => void save()}
          placeholder={placeholder}
          data-testid={testId ? `${testId}-input` : undefined}
          aria-label={ariaLabel}
          {...textareaAttrs}
        />
      ) : (
        <input
          ref={(el) => {
            inputRef.current = el;
          }}
          className="ief-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => void save()}
          placeholder={placeholder}
          data-testid={testId ? `${testId}-input` : undefined}
          aria-label={ariaLabel}
          {...inputAttrs}
        />
      )}
      {error && (
        <p className="ief-error" data-testid={testId ? `${testId}-error` : undefined}>
          {error}
        </p>
      )}
      {/* Single-line inputs commit on Enter — universal browser
          behaviour the user already knows. The hint only renders for
          multiline (textarea) where Cmd/Ctrl+Enter is non-obvious.
          NN/g "Adaptive Help": don't shout the obvious. */}
      {multiline && (
        <p className="ief-hint">
          <kbd>⌘/Ctrl</kbd>+<kbd>Enter</kbd> guarda · <kbd>Esc</kbd> cancela
        </p>
      )}
    </div>
  );
}
